<template>
  <v-menu
    v-model="menuVisible"
    offset-y
    @keydown.esc="menuVisible = false"
  >
    <template v-slot:activator="{ on }">
      <div>
        <v-btn
          rounded
          text
          outlined
          class="text-none"
          v-on="on"
        >
          <v-icon
            x-small
            class="mr-2"
          >
            far fa-pen
          </v-icon>
          {{ $t('button.edit_columns') }}
        </v-btn>
      </div>
    </template>

    <v-card :width="showAggregation ? 300 : 250">
      <v-text-field
        v-model="search"
        dense
        outlined
        hide-details
        :label="$t('text_input.search.placeholder')"
        class="pa-2"
        clearable
        @click.stop
      />
      <div style="overflow: auto; flex-grow: 1; height: 300px">
        <draggable
          :list="selectedVariables"
          handle=".handle"
          :group="{ name: 'variables' }"
          :invert-swap="true"
          :swap-treshold="0.6"
          :animation="100"
          @update="updateOrder"
          @change="updateOrder"
        >
          <div
            v-for="(variable, index) in selectedVariables"
            :key="variable.value + variable.aggregation + index"
          >
            <div>
              <v-list-item
                @click.stop="unselectVariable(variable)"
              >
                <v-list-item-action>
                  <v-checkbox
                    :input-value="true"
                    @click.stop="unselectVariable(variable)"
                  />
                </v-list-item-action>
                <v-list-item-content class="pa-0">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-list-item-title
                        v-bind="attrs"
                        class="text-truncate"
                        v-on="on"
                      >
                        {{ $t(`variables.${variable.value}`) }}
                        <span
                          v-if="variable.aggregation"
                          class="text-caption grey--text"
                        >({{ aggregations.find(agg => agg.value === variable.aggregation).text }})</span>
                      </v-list-item-title>
                    </template>
                    <span>
                      {{ $t(`variables.${variable.value}`) }}
                      <span v-if="variable.aggregation">
                        ({{ aggregations.find(agg => agg.value === variable.aggregation).text }})
                      </span>
                    </span>
                  </v-tooltip>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon
                    small
                    class="handle"
                    style="cursor: move"
                  >
                    far fa-grip-lines
                  </v-icon>
                </v-list-item-action>
              </v-list-item>
            </div>
          </div>
        </draggable>
        <v-divider v-if="filteredUnselectedVariables.length" />
        <v-list>
          <v-list-item
            v-for="(variable, index) in filteredUnselectedVariables"
            :key="variable.value + variable.aggregation + index"
            @click.stop="selectVariable(variable)"
          >
            <v-list-item-action>
              <v-checkbox
                :value="false"
                @click.stop="selectVariable(variable)"
              />
            </v-list-item-action>
            <v-list-item-content class="pa-0">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-list-item-title
                    v-bind="attrs"
                    class="text-truncate"
                    v-on="on"
                  >
                    {{ $t(`variables.${variable.value}`) }}
                    <span
                      v-if="variable.aggregation"
                      class="text-caption grey--text"
                    >({{ aggregations.find(agg => agg.value === variable.aggregation).text }})</span>
                  </v-list-item-title>
                </template>
                <span>
                  {{ $t(`variables.${variable.value}`) }}
                  <span v-if="variable.aggregation">
                    ({{ aggregations.find(agg => agg.value === variable.aggregation).text }})
                  </span>
                </span>
              </v-tooltip>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import draggable from 'vuedraggable'
import { cloneDeep } from 'lodash'

export default {
  name: 'VariableSelector',
  components: { draggable },
  props: {
    variables: {
      type: Array,
      required: true
    },
    layoutVariables: {
      type: Array,
      required: true
    },
    showAggregation: {
      type: Boolean,
      default: false
    },
    excludedVariables: {
      type: Array,
      default: () => []
    }
  },
  data: () => ({
    selectedVariables: [],
    unselectedVariables: [],
    menuVisible: false,
    search: '',
    initialized: false
  }),
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    filteredUnselectedVariables() {
      if (this.search === null) return this.unselectedVariables

      return this.unselectedVariables.filter(v => v.text.toLowerCase().includes(this.search.toLowerCase()))
    },
    aggregations() {
      return [
        { value: 'DL', text: this.$t('variables.DL.long') },
        { value: 'WL', text: this.$t('variables.WL.long') },
        { value: 'WC', text: this.$t('variables.WC.long') },
        { value: 'AL', text: this.$t('variables.AL.long') },
        { value: 'CL', text: this.$t('variables.CL.long') },
        { value: 'ACWR', text: this.$t('variables.ACWR.long') },
        { value: 'FRES', text: this.$t('variables.FRES.long') },
        { value: 'MON', text: this.$t('variables.MON.long') },
        { value: 'STR', text: this.$t('variables.STR.long') }
      ]
    }
  },
  watch: {
    selectedVariables: {
      deep: true,
      handler: function (newValue) {
        this.$emit('update:layoutVariables', newValue)
      }
    },
    variables: {
      immediate: true,
      handler: function (newVariables) {
        if (newVariables && newVariables.length > 0 && !this.initialized) {
          this.initializeVariables()
          this.initialized = true
        }
      }
    },
    menuVisible() {
      if (this.menuVisible && !this.initialized) {
        this.initializeVariables()
      }
    }
  },
  methods: {
    initializeVariables() {
      const filteredVariables = this.variables.filter(v => !this.excludedVariables.includes(v.value))

      const variablesWithAggregations = this.generateVariablesWithAggregations(filteredVariables)

      if (this.showAggregation) {
        this.selectedVariables = cloneDeep(this.layoutVariables)
        this.unselectedVariables = variablesWithAggregations.filter(v => !this.layoutVariables.find(lv => lv.id === v.id && lv.aggregation === v.aggregation))
      } else {
        this.selectedVariables = cloneDeep(this.layoutVariables)
        this.unselectedVariables = filteredVariables.filter(v => !this.layoutVariables.find(lv => lv.id === v.id))
      }
    },
    selectVariable(variable) {
      this.unselectedVariables.splice(this.unselectedVariables.indexOf(variable), 1)
      variable.order = this.selectedVariables.length + 1
      this.selectedVariables.push(variable)

      this.updateOrder()
    },
    unselectVariable(variable) {
      this.selectedVariables.splice(this.selectedVariables.indexOf(variable), 1)
      variable.order = null
      this.unselectedVariables.push(variable)

      this.updateOrder()
    },
    updateOrder(event) {
      this.selectedVariables.forEach((variable, index) => {
        variable.order = index + 1
      })

      this.$emit('update')
    },
    generateVariablesWithAggregations(variables) {
      if (!this.showAggregation) return variables

      const variablesWithAggregations = []

      variables.forEach(v => {
        if (v.category !== 'WELLNESS' && v.value !== 'pain' && v.value !== 'reason') {
          this.aggregations.forEach(agg => {
            const newVariable = cloneDeep(v)
            newVariable.aggregation = agg.value
            variablesWithAggregations.push(newVariable)
          })
        } else {
          variablesWithAggregations.push(cloneDeep(v))
        }
      })

      return variablesWithAggregations
    }
  }
}
</script>

<style scoped>

</style>
